import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PdfButtonClickedEvent } from '@home/src/app/Interface/IPdfGeneration';
import { NadaSpinnerComponent } from '@shared/components/nada-spinner/nada-spinner.component';
import { PdfViewerService } from '@home/src/app/core/services/pdf/pdf-viewer.service';
import { Subject, takeUntil } from 'rxjs';
import { CustomDropdownComponent } from '../custom-dropdown/custom-dropdown.component';
import { CustomSwitchComponent } from '../custom-switch/custom-switch.component';

@Component({
  selector: 'app-excelpdfpopup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NadaSpinnerComponent,
    CustomDropdownComponent,
    CustomSwitchComponent,
  ],
  templateUrl: './excelpdfpopup.component.html',
  styleUrl: './excelpdfpopup.component.css',
})
export class ExcelpdfpopupComponent implements OnInit, OnChanges, OnDestroy {
  @Input() title: string = '';
  @Input() mode: 'excel' | 'pdf' = 'excel';
  @Input() options: { label: string; checked: boolean }[] = [];
  @Input() isPageLayoutEnabled: boolean = false; // New property for layout toggle
  @Input() PageLayout: boolean = false; // New property for layout toggle
  @Input() reportTitle: string = '';
  @Input() pageSizeOptions: string[] = ['Letter', 'Legal'];
  @Input() orientationOptions: string[] = ['Portrait', 'Landscape'];
  pageSize: string = this.pageSizeOptions[0]; // Default to first option
  orientation: string = this.orientationOptions[0]; // Default to first option
  isLoading: boolean = false; // New spinner flag
  @Output() closeClicked = new EventEmitter<void>();
  @Output() primaryButtonClicked = new EventEmitter<PdfButtonClickedEvent>();
  @Output() loadingComplete = new EventEmitter<void>(); // Notify parent when loading is done

  constructor(
    private pdfService: PdfViewerService,
    private cdr: ChangeDetectorRef,
  ) {}
  private readonly unsubscribe$ = new Subject<void>();

  progressMessage: string = '';

  ngOnInit() {
    if (this.PageLayout) {
      this.orientation = 'Landscape';
    } else {
      this.orientation = 'Portrait';
    }
    this.pdfService.updateProgressMessage('');
    this.pdfService.progressMessage$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((state) => {
        this.progressMessage = state;
        this.cdr.detectChanges();
      });
  }

  onCloseClicked(event: MouseEvent) {
    event.stopPropagation();
    this.closeClicked.emit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['PageLayout']) {
      this.orientation = this.PageLayout ? 'Landscape' : 'Portrait';
    }
  }

  onPrimaryButtonClicked(event: MouseEvent) {
    event.stopPropagation();
    if (this.isLoading) return; // Prevent multiple clicks
    this.isLoading = true;
    const selectedOptions = this.options
      .filter((opt) => opt.checked)
      .map((opt) => opt.label);
    const eventData: PdfButtonClickedEvent = {
      mode: this.mode,
      selectedOptions,
      isPageLayoutEnabled: this.isPageLayoutEnabled,
      pageSize: this.pageSize,
      orientation: this.orientation,
      reportTitle: this.reportTitle,
      PageLayout: this.PageLayout,
    };
    this.primaryButtonClicked.emit(eventData);
  }

  togglePageLayout() {
    if (this.isPageLayoutEnabled) {
      this.reportTitle = this.reportTitle ?? '';
      this.pageSize = this.pageSize ?? this.pageSizeOptions[0];
      this.orientation = this.orientation ?? this.orientationOptions[0];
    }
  }

  restrictSpecialChars(event: KeyboardEvent) {
    const regex = /^[a-zA-Z0-9]+$/; // Allow letters, numbers, and spaces
    if (!regex.test(event.key)) {
      event.preventDefault(); // Block invalid characters
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.complete();
  }
}
